import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import router from '@/routes';

import ApiService from '../services/ApiService';
import keycloak from '../services/keycloak';

import { useAuthCommonStore } from './authCommonStore';
import { LoginRequest, AuthUser, RefreshLoginRequest, LogoutRequest } from '../models/AuthModels';
import { useJwtInfo } from '@/modules/auth/composables/useJwtInfo';

export const useAuthStore: any = defineStore('authStore', () => {
  const commonStore = useAuthCommonStore();

  const kc = keycloak;
  const authUser = ref(
    sessionStorage.getItem('authUser') != null && sessionStorage.getItem('authUser') !== undefined ? JSON.parse(sessionStorage.authUser) : null
  );

  const token = ref<string>('');
  const refreshToken = ref<string>('');

  const username = ref<string>('');
  const name = ref<string>('');
  const email = ref<string>('');

  const roles = ref<string[]>([]);

  const initialized = ref<boolean>(false);
  let initializationPromise: Promise<void> | null = null;
  let refreshPromise: Promise<void> | null = null;

  const keycloakInitOptions = {
    onLoad: 'check-sso' as const,
    checkLoginIframe: false,
    pkceMethod: 'S256' as const,
  };

  // const isAuthenticated = computed(() => kc.authenticated);
  const isAuthenticated = computed(() => {
    if (kc.authenticated) {
      return true;
    } else if (authUser.value != null && authUser.value.lagerToken != null) {
      return true;
    } else {
      return false;
    }
  });

  const isAuthorized = computed(() => roles.value.includes('access-ewerp'));
  const isEwerpAdmin = computed(() => kc.authenticated === true && roles.value.includes('ewerp-admin'));

  function resetAuthState() {
    authUser.value = null;

    token.value = '';
    refreshToken.value = '';

    username.value = '';
    name.value = '';
    email.value = '';
    roles.value = [];
  }

  async function logout() {
    if (kc.authenticated) {
      keycloakLogout();
    } else if (authUser.value != null) {
      erpLogout();
    } else {
      resetAuthState();
      sessionStorage.clear();
      router.push({ name: 'auth.login' });
    }
  }

  function isTokenExpired(requestUrl: string, minValidity: number = 60): boolean {
    if (kc.authenticated && kc.token && kc.isTokenExpired(minValidity)) {
      return true;
    } else if (authUser.value != null && authUser.value.lagerToken != null) {
      const { secondsLeft } = useJwtInfo(authUser.value.lagerToken);
      if (secondsLeft.value !== null && secondsLeft.value <= minValidity && !requestUrl.includes('/login/refresh')) {
        return true;
      }
    }

    return false;
  }

  async function refreshLogin() {
    // Mehrere parallele API-Aufrufe dürfen nicht jeweils einen eigenen Refresh starten.
    if (refreshPromise !== null) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      try {
        if (kc.authenticated) {
          await keycloakUpdateToken(60);
        } else if (authUser.value != null && authUser.value.lagerToken != null) {
          await erpRefreshLogin();
        } else {
          handleFailedRefresh();
        }
      } catch (error) {
        // Ein abgelaufener oder ungültiger Refresh-Token muss immer direkt
        // zur Abmeldung führen – auch bei Requests aus einem Timer.
        handleFailedRefresh();
        throw error;
      }
    })();

    try {
      await refreshPromise;
    } finally {
      refreshPromise = null;
    }
  }

  function handleFailedRefresh() {
    const wasKeycloakAuthenticated = kc.authenticated === true;
    const loginUrl = window.location.origin + router.resolve({ name: 'auth.login' }).href;

    resetAuthState();
    sessionStorage.clear();
    localStorage.clear();

    if (wasKeycloakAuthenticated) {
      // Keycloak führt bei Erfolg selbst den Redirect auf loginUrl aus.
      // Nicht abwarten: Ein hängender Keycloak-Request darf den Timer-Request
      // und damit die Abmeldung nicht blockieren.
      void kc.logout({ redirectUri: loginUrl }).catch(() => {
        kc.clearToken();
        window.location.replace(loginUrl);
      });
      return;
    }

    // Vollständige Navigation, damit sie nicht von einem noch laufenden
    // Hintergrund-Request oder einer Komponentenbehandlung überlagert wird.
    window.location.replace(loginUrl);
  }

  async function initAuth() {
    if (initialized.value) return;

    if (initializationPromise !== null) {
      return initializationPromise;
    }

    initializationPromise = initializeAuth();

    try {
      await initializationPromise;
    } finally {
      initializationPromise = null;
    }
  }

  async function initializeAuth() {
    if (sessionStorage.getItem('authUser') != null && sessionStorage.getItem('authUser') !== undefined) {
      const user: AuthUser = JSON.parse(sessionStorage.authUser);
      setAuthStatusFromAuthUser(user);
      initialized.value = true;
      return;
    }

    try {
      const authenticated = await initializeKeycloak();

      // sobald init durch ist, Fragment aus der URL löschen
      if (window.location.hash.includes('code=')) {
        history.replaceState(history.state, document.title, window.location.pathname + window.location.search);
      }

      // console.log('initAuth → authenticated:', authenticated, 'kc.authenticated:', kc.authenticated, 'token length:', kc.token?.length);
      initialized.value = true;
      if (authenticated && kc.token) {
        // console.log(kc);
        token.value = kc.token;
        refreshToken.value = kc.refreshToken || '';
        roles.value = kc.tokenParsed?.realm_access?.roles || [];
        username.value = kc.idTokenParsed?.preferred_username || '';
        name.value = kc.idTokenParsed?.name || '';
        email.value = kc.idTokenParsed?.email || '';

        // Nach erfolgreichem SSO-Login zur Bestellungen-Seite weiterleiten, wenn man derzeit auf der Login-Seite ist
        if (router.currentRoute.value.name === 'auth.login') {
          router.push({ name: 'home' });
        }
      }
    } catch (error) {
      console.error('Keycloak initialization failed:', error);
    }
  }

  async function initializeKeycloak(): Promise<boolean> {
    if (kc.didInitialize) {
      return kc.authenticated === true;
    }

    return kc.init(keycloakInitOptions);
  }

  function hasRoleOrAdmin(role: string): boolean {
    return roles.value.includes(role) || isEwerpAdmin.value;
  }

  //#region Keycloak
  async function keycloakLogin() {
    sessionStorage.clear(); // Session-Storage leeren, um alte Daten zu entfernen
    resetAuthState();

    await initializeKeycloak();

    // const redirectUri = window.location.origin + router.resolve({ name: 'ewwms.bestellungen' }).href;
    const redirectUri = window.location.origin;
    return kc.login({ redirectUri: redirectUri });
  }

  function keycloakLogout() {
    sessionStorage.clear(); // Session-Storage leeren, um alte Daten zu entfernen
    // Keycloak kümmert sich um Redirect und Session-Cleanup

    // redirectUri soll die Login-Seite sein
    const redirectUri = window.location.origin + router.resolve({ name: 'auth.login' }).href;
    return kc.logout({ redirectUri: redirectUri });
  }

  async function keycloakUpdateToken(minValidity: number = 60) {
    const refreshed = await kc.updateToken(minValidity);
    if (refreshed && kc.token) {
      token.value = kc.token!;
    }

    return refreshed;
  }
  //#endregion

  //#region EW ERP API
  function setAuthStatusFromAuthUser(user: AuthUser) {
    authUser.value = user;

    token.value = user.lagerToken;
    refreshToken.value = user.refreshToken;
    username.value = user.lagerUser.username;
    name.value = user.lagerUser.mitarbeitername;
    // email.value = user.lagerUser.email;
    const tokenInfo = useJwtInfo(user.lagerToken);
    const rolesFromToken = tokenInfo.parsedToken.value?.realm_access?.roles;

    roles.value = rolesFromToken || [];

    sessionStorage.setItem('authUser', JSON.stringify(user));
  }

  async function erpLogin(data: LoginRequest) {
    commonStore.setStatus({ ...commonStore.status, authLoading: true, authErrorMsg: '' });

    return ApiService.login(data)
      .then((res) => {
        const user: AuthUser = res.data;

        // Parameter setzen
        setAuthStatusFromAuthUser(user);

        router.push({ name: 'home' });
      })
      .catch((error) => {
        commonStore.status.authErrorMsg = error.response.data.title;
        sessionStorage.removeItem('authUser');
      })
      .finally(() => {
        commonStore.setStatus({ ...commonStore.status, authLoading: false });
      });
  }

  async function erpRefreshLogin() {
    const request = new RefreshLoginRequest(refreshToken.value);
    const res = await ApiService.refreshLogin(request);

    const user: AuthUser = res.data;

    // Parameter setzen
    setAuthStatusFromAuthUser(user);

    return;
  }

  async function erpLogout() {
    commonStore.setStatus({ ...commonStore.status, authLoading: true, authErrorMsg: '' });

    return ApiService.logout(new LogoutRequest(refreshToken.value))
      .then(() => {
        console.log('LOGOUT');
      })
      .catch(() => {
        console.log('LOGOUT CATCH');
      })
      .finally(async () => {
        commonStore.status.authLoading = false;
        resetAuthState();
        sessionStorage.clear();
        localStorage.clear();
        router.push({ name: 'auth.login' });
      });
  }
  //#endregion

  return {
    kc,
    token,
    username,
    name,
    email,
    roles,
    authUser,

    isAuthenticated,
    isAuthorized,
    isEwerpAdmin,
    hasRoleOrAdmin,

    logout,
    refreshLogin,
    isTokenExpired,

    //#region Keycloak
    initAuth,
    keycloakLogin,
    keycloakLogout,
    keycloakUpdateToken,
    //#endregion

    //#region EW ERP API
    erpLogin,
    erpRefreshLogin,
    erpLogout,
    //#endregion
  };
});
